// Payments service layer for admin operations
// Uses bookings table which stores payment information
import { supabase } from '@/integrations/supabase/client';

export interface Payment {
  id: string;
  booking_id: string;
  full_name: string;
  email: string;
  phone: string;
  service: string;
  appointment_start: string | null;
  appointment_type: string | null;
  payment_method: string | null;
  payment_status: string | null;
  payment_amount: number | null;
  payment_currency: string | null;
  payment_source: 'online' | 'offline' | null;
  payment_notes: string | null;
  payment_date: string | null;
  stripe_session_id: string | null;
  payment_evidence_url: string | null;
  zelle_confirmation: string | null;
  created_at: string;
  updated_at: string;
}

export type PaymentStatus = 'pending' | 'pending_verification' | 'paid' | 'confirmed' | 'cancelled' | 'failed' | 'refunded';

export interface GetPaymentsParams {
  from?: string;
  to?: string;
  status?: string;
  search?: string;
  page?: number;
  pageSize?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export interface GetPaymentsResult {
  data: Payment[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

// Get payments with filters, pagination, and sorting
export async function getPayments(params: GetPaymentsParams = {}): Promise<GetPaymentsResult> {
  const {
    from,
    to,
    status,
    search,
    page = 1,
    pageSize = 20,
    sortBy = 'created_at',
    sortOrder = 'desc',
  } = params;

  let query = supabase
    .from('bookings')
    .select('id, booking_id, full_name, email, phone, service, appointment_start, appointment_type, payment_method, payment_status, payment_amount, payment_currency, payment_source, payment_notes, payment_date, stripe_session_id, payment_evidence_url, zelle_confirmation, created_at, updated_at', { count: 'exact' });

  // Apply date range filter on created_at (payment date)
  if (from) {
    query = query.gte('created_at', from);
  }
  if (to) {
    query = query.lte('created_at', to);
  }

  // Apply status filter
  if (status && status !== 'all') {
    query = query.eq('payment_status', status);
  }

  // Apply search filter (email, booking_id, stripe_session_id)
  if (search) {
    query = query.or(
      `email.ilike.%${search}%,booking_id.ilike.%${search}%,stripe_session_id.ilike.%${search}%,full_name.ilike.%${search}%`
    );
  }

  // Apply sorting
  query = query.order(sortBy, { ascending: sortOrder === 'asc' });

  // Apply pagination
  const start = (page - 1) * pageSize;
  const end = start + pageSize - 1;
  query = query.range(start, end);

  const { data, error, count } = await query;

  if (error) {
    console.error('Error fetching payments:', error);
    throw error;
  }

  return {
    data: (data || []) as Payment[],
    total: count || 0,
    page,
    pageSize,
    totalPages: Math.ceil((count || 0) / pageSize),
  };
}

// Get single payment by ID (with full booking details)
export async function getPaymentById(id: string): Promise<Payment | null> {
  const { data, error } = await supabase
    .from('bookings')
    .select('*')
    .eq('id', id)
    .single();

  if (error) {
    console.error('Error fetching payment:', error);
    throw error;
  }

  return data as Payment;
}

// Get payment stats
export async function getPaymentStats() {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
  const yearStart = new Date(today.getFullYear(), 0, 1);

  // Get total successful payments
  const { data: successfulData, error: successfulError } = await supabase
    .from('bookings')
    .select('payment_amount')
    .in('payment_status', ['paid', 'confirmed', 'completed']);

  if (successfulError) {
    console.error('Error fetching successful payments:', successfulError);
  }

  const totalRevenue = successfulData?.reduce((sum, item) => sum + (item.payment_amount || 0), 0) || 0;

  // Get this month's revenue
  const { data: monthData, error: monthError } = await supabase
    .from('bookings')
    .select('payment_amount')
    .in('payment_status', ['paid', 'confirmed', 'completed'])
    .gte('created_at', monthStart.toISOString());

  if (monthError) {
    console.error('Error fetching monthly payments:', monthError);
  }

  const monthlyRevenue = monthData?.reduce((sum, item) => sum + (item.payment_amount || 0), 0) || 0;

  // Get successful count
  const { count: successfulCount } = await supabase
    .from('bookings')
    .select('*', { count: 'exact', head: true })
    .in('payment_status', ['paid', 'confirmed', 'completed']);

  // Get pending count (includes pending_verification)
  const { count: pendingCount } = await supabase
    .from('bookings')
    .select('*', { count: 'exact', head: true })
    .in('payment_status', ['pending', 'pending_verification']);

  // Get failed count
  const { count: failedCount } = await supabase
    .from('bookings')
    .select('*', { count: 'exact', head: true })
    .eq('payment_status', 'failed');

  return {
    totalRevenue: totalRevenue / 100, // Convert from cents
    monthlyRevenue: monthlyRevenue / 100, // Convert from cents
    successfulCount: successfulCount || 0,
    pendingCount: pendingCount || 0,
    failedCount: failedCount || 0,
  };
}

// Format currency
export function formatCurrency(amount: number | null, currency: string = 'USD'): string {
  if (amount === null || amount === undefined) return '$0.00';
  const value = amount / 100; // Convert from cents
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency,
  }).format(value);
}

// Status options with styling
export const PAYMENT_STATUS_OPTIONS = [
  { value: 'pending', label: 'Pending', variant: 'warning' as const },
  { value: 'pending_verification', label: 'Pending Verification', variant: 'warning' as const },
  { value: 'paid', label: 'Paid', variant: 'success' as const },
  { value: 'confirmed', label: 'Confirmed', variant: 'success' as const },
  { value: 'completed', label: 'Completed', variant: 'default' as const },
  { value: 'cancelled', label: 'Cancelled', variant: 'destructive' as const },
  { value: 'failed', label: 'Failed', variant: 'destructive' as const },
  { value: 'refunded', label: 'Refunded', variant: 'outline' as const },
];

// Get status styling
export function getStatusStyle(status: string | null): { variant: string; label: string } {
  const option = PAYMENT_STATUS_OPTIONS.find(opt => opt.value === status);
  return option || { variant: 'secondary', label: status || 'Unknown' };
}
